<script lang="ts">
    import type {
        DocumentDetailReactivePageData,
        DocumentDetailStaticPageData
    } from 'src/features/record/kp-document-detail-page/types';
    import {getPageContext} from 'shared/layouts/page-context';
    import {getInjector, getLocalization} from 'core/svelte-context/context';
    import {DocumentDetailTabsetService} from 'src/features/record/kp-document-detail-tabset/document-detail-tabset.service';
    import {onMount} from 'svelte';
    import {pipe} from 'core/utils';
    import {loc} from 'shared/utils/pipes';
    import {getSutorFondIcon} from '../sutor-fond-icon-lookup';
    import {exists} from 'shared/utils/custom-utils';
    import {RecordFieldsDetailGrid} from 'src/features/erp-sutor/features/record-fields-detail-grid';
    import KpMasonryGridItem from 'shared/layouts/masonry-grid/KpMasonryGridItem.svelte';
    import KpMasonryGrid from 'shared/layouts/masonry-grid/KpMasonryGrid.svelte';
    import KpGenericPanel from 'shared/ui-widgets/panel/KpGenericPanel.svelte';
    import KpRecordMarcTable from '../../../../record/kp-record-marc-table/KpRecordMarcTable.svelte';
    import KpRecordOperationMini from 'src/features/record/operation/KpRecordOperationMini.svelte';
    import KpPaginatedPanelList from 'shared/components/kp-paginated-panel-list/KpPaginatedPanelList.svelte';
    import Flex from 'shared/layouts/flex/Flex.svelte';
    import KpHeading from 'shared/components/kp-heading/KpHeading.svelte';
    import Spacer from 'shared/layouts/flex/Spacer.svelte';
    import GenericRecordIcon from '../components/GenericRecordIcon.svelte';

    const service = getInjector().getByClass(DocumentDetailTabsetService);
    const pageContext = getPageContext<DocumentDetailStaticPageData, DocumentDetailReactivePageData>();
    const model = pageContext.staticData.model;
    const localize = getLocalization();

    const operationPageableList = service.createOperationPageableList(model.record);
    const recordIcon = getSutorFondIcon(model.record.fond);

    onMount(() => {
        operationPageableList.loadFirstPage();
    });
</script>

<Flex direction="column">
    <Flex direction="row" alignItems="center">
        {#if exists(recordIcon)}
            <GenericRecordIcon icon={recordIcon} fond={model.record.fond}/>
            <Spacer direction="horizontal" size="ml"/>
        {/if}

        <Flex direction="row" alignItems="baseline">
            <KpHeading type="h1">{model.record.text}</KpHeading>
            <Spacer direction="horizontal" size="ml"/>
            <small class="text-muted">Fond '{pipe(model.record.fond, loc())}' (ID {model.record.fond.id})</small>
        </Flex>
    </Flex>
    <Spacer direction="vertical" size="xl"/>
    <Spacer direction="vertical" size="xxl"/>

    <RecordFieldsDetailGrid record={model.record}/>
    <Spacer direction="vertical" size="xxl"/>
    <Spacer direction="vertical" size="xxl"/>

    <KpHeading type="h2">Dodatečné informace</KpHeading>
    <Spacer direction="vertical" size="xxl"/>

    <KpMasonryGrid gridTemplateColumns="repeat(auto-fill, minmax(600px, 1fr))"
                   additionalClasses="document-detail-grid">

        <KpMasonryGridItem animate={false}>
            <KpGenericPanel hasBodyPadding={false}>
                <span slot="heading">Systemist debug - MARC informace</span>
                <KpRecordMarcTable fields={model.record.fields}/>
            </KpGenericPanel>
        </KpMasonryGridItem>

        <KpMasonryGridItem animate={false}>
            <KpGenericPanel>
                <span slot="heading">{localize(/* @kp-localization record.ProcessingHistory */ 'record.ProcessingHistory')}</span>
                <KpPaginatedPanelList pageableList={operationPageableList}
                                      isInPaddedPanel={true}
                                      let:item>
                    <KpRecordOperationMini operation={item}/>
                </KpPaginatedPanelList>
            </KpGenericPanel>
        </KpMasonryGridItem>
    </KpMasonryGrid>
</Flex>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    :global {
        .document-detail-grid .marc-table {
            tr > th {
                padding: @spacing-sm @spacing-ml;
                font-weight: 500;
            }

            &.table-color-accented > thead > tr > th {
                color: @themed-text-default;
                border-bottom: 1px solid @themed-border-default;
            }
        }
    }
</style>